import React, { useCallback, useEffect, useState } from 'react';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Alert,
  Table,
  TextInput,
  Select,
  Modal,
  ActionIcon,
  Loader
} from '@mantine/core';
import { IconArrowLeft, IconPlus, IconTrash, IconRefresh } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import {
  listAllowlist,
  createAllowlistEntry,
  deleteAllowlistEntry
} from '../api_scripts/allowlistClient';
import { isSitBackendConfigured } from '../api_scripts/sitApiClient';

// Informational tags only — no longer enforced. Useful as audit metadata.
const CONSENT_OPTIONS = [
  { value: 'our-own-number', label: 'Our own number' },
  { value: 'customer-signed-test-agreement', label: 'Customer-signed test agreement' },
  { value: 'vendor-published-test-ivr', label: 'Vendor-published test IVR' },
  { value: 'sandbox-lab', label: 'Sandbox / lab number' }
];

const E164 = /^\+[1-9][0-9]{6,14}$/;

function AllowlistManager({ onBack }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({
    did: '',
    consentBasis: null,
    owner: '',
    purpose: '',
    expiresAt: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const backendConfigured = isSitBackendConfigured();

  const reload = useCallback(async () => {
    if (!backendConfigured) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listAllowlist();
      setEntries(data);
    } catch (err) {
      setError(err?.response?.data?.message ?? err.message ?? 'Failed to load allowlist.');
    } finally {
      setLoading(false);
    }
  }, [backendConfigured]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleSubmit = async () => {
    if (!E164.test(form.did)) {
      notifications.show({
        title: 'Invalid DID',
        message: 'Use E.164 format, e.g. +15551234567',
        color: 'red'
      });
      return;
    }
    if (!form.owner) {
      notifications.show({ title: 'Owner required', message: 'Set an owner.', color: 'red' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = { did: form.did, owner: form.owner };
      if (form.consentBasis) payload.consentBasis = form.consentBasis;
      if (form.purpose) payload.purpose = form.purpose;
      if (form.expiresAt) payload.expiresAt = new Date(form.expiresAt).toISOString();
      await createAllowlistEntry(payload);
      notifications.show({ title: 'Allowlist updated', message: form.did, color: 'green' });
      setModalOpen(false);
      setForm({ did: '', consentBasis: null, owner: '', purpose: '', expiresAt: '' });
      reload();
    } catch (err) {
      notifications.show({
        title: 'Add failed',
        message: err?.response?.data?.message ?? err.message,
        color: 'red'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (did) => {
    if (!window.confirm(`Remove ${did} from the allowlist?`)) return;
    try {
      await deleteAllowlistEntry(did);
      notifications.show({ title: 'Removed', message: did, color: 'green' });
      reload();
    } catch (err) {
      notifications.show({
        title: 'Delete failed',
        message: err?.response?.data?.message ?? err.message,
        color: 'red'
      });
    }
  };

  return (
    <Container size="lg" py="lg">
      <Group justify="space-between" mb="md">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onBack}>
          Back to SIT Home
        </Button>
        <Group>
          <ActionIcon variant="light" onClick={reload} title="Refresh" disabled={!backendConfigured}>
            <IconRefresh size={16} />
          </ActionIcon>
          <Button
            color="violet"
            leftSection={<IconPlus size={16} />}
            onClick={() => setModalOpen(true)}
            disabled={!backendConfigured}
          >
            Add entry
          </Button>
        </Group>
      </Group>

      <Stack gap="md">
        <Title order={2}>Destination Allowlist</Title>

        {!backendConfigured && (
          <Alert color="yellow">
            The SIT backend URL is not configured. Set <code>REACT_APP_SIT_API_BASE</code> in your
            <code> .env</code> to enable allowlist management.
          </Alert>
        )}

        {error && <Alert color="red">{error}</Alert>}

        <Card withBorder padding="lg" radius="md">
          {loading ? (
            <Group justify="center" py="xl">
              <Loader />
            </Group>
          ) : entries.length === 0 ? (
            <Text size="sm" color="dimmed">No entries.</Text>
          ) : (
            <Table verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>DID</Table.Th>
                  <Table.Th>Consent basis</Table.Th>
                  <Table.Th>Owner</Table.Th>
                  <Table.Th>Purpose</Table.Th>
                  <Table.Th>Expires</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {entries.map((e) => (
                  <Table.Tr key={e.did}>
                    <Table.Td><code>{e.did}</code></Table.Td>
                    <Table.Td>{e.consentBasis}</Table.Td>
                    <Table.Td>{e.owner}</Table.Td>
                    <Table.Td>{e.purpose || '—'}</Table.Td>
                    <Table.Td>{e.expiresAt ? new Date(e.expiresAt).toLocaleString() : '—'}</Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(e.did)} title="Delete">
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </Stack>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title="Add allowlist entry" centered>
        <Stack gap="md">
          <TextInput
            label="Destination DID (E.164)"
            placeholder="+15551234567"
            value={form.did}
            onChange={(e) => setForm({ ...form, did: e.currentTarget.value })}
            required
          />
          <Select
            label="Consent basis (optional)"
            description="Informational only — used for audit/reporting, not enforced."
            data={CONSENT_OPTIONS}
            value={form.consentBasis}
            onChange={(v) => setForm({ ...form, consentBasis: v ?? null })}
            clearable
          />
          <TextInput
            label="Owner (email)"
            placeholder="paul@presidio.com"
            value={form.owner}
            onChange={(e) => setForm({ ...form, owner: e.currentTarget.value })}
            required
          />
          <TextInput
            label="Purpose (optional)"
            placeholder="Regression suite — Presidio sandbox IVR"
            value={form.purpose}
            onChange={(e) => setForm({ ...form, purpose: e.currentTarget.value })}
          />
          <TextInput
            label="Expires (optional, ISO datetime)"
            placeholder="2026-12-31T00:00:00Z"
            value={form.expiresAt}
            onChange={(e) => setForm({ ...form, expiresAt: e.currentTarget.value })}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button color="violet" onClick={handleSubmit} loading={submitting}>Add</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default AllowlistManager;
